// A row's note and conversation, without knowing where its data comes from.
//
// **It opens the row rather than floating over it.** It was a 24rem popover pinned
// to the mark, and three things were wrong with that: it covered the rows beneath it
// while you read it, the only thing tying it to its row was a hover tint, and prose
// that now renders markdown had 40 characters of measure to do it in. So the row
// grows instead, and what appears is a panel in the app's GROUND colour inside the
// board's paper — bordered and inset, a container the row opened rather than a row
// that changed colour.
//
// That is why this file exports two pieces instead of a component. The mark sits in
// the row's last cell; the panel is a sibling of the row in the board's grid,
// spanning every column. They cannot be one element: a cell cannot be full width, and
// a panel inside one would be clipped to it. The row that owns them holds the open
// state.
//
// The same split as `BoardSheet`, for the same reason: the team's card reads the
// store and dispatches events, the client's posts to its own endpoint, and if each
// drew its own the two would drift until the client was arguing with a row in a panel
// that no longer looked like the one the team replies in.
//
// The NOTE is a slot rather than a prop, because the two editors are genuinely
// different: the team's is `RichField`, which carries @mentions and therefore the
// store; the client's is a plain box. Everything else is here.

import { useEffect, useRef, useState, type ReactNode } from 'react'

import { Button } from './common'
import { Icon } from './Icon'
import { Markdown } from './Markdown'

export interface CardComment {
  id: string
  body: string
  at: string
  /** who wrote it, already resolved to a display name */
  by: string
  /** rendered as the reader's own — "you", on whichever side of the wire */
  mine: boolean
}

/** Long enough that crossing the column does not strobe cards, short enough that
 *  hovering on purpose feels answered. */
const PEEK_DELAY = 320

/**
 * The mark, in the row's last cell — and the quick look it gives on the way past.
 *
 * Two gestures, two depths. **Hover peeks**: a read-only card, wider than the old
 * popover because prose needs the measure, showing the note and the last of the
 * thread; it is `pointer-events: none`, so it can never eat the click it is
 * advertising or trap the pointer on its way somewhere else. **Click opens**: the row
 * grows and the panel below it is the one you can type in.
 *
 * Touch has no hover, so the peek simply never appears there — which is why it holds
 * nothing you cannot get by tapping.
 *
 * The mark itself is always there when there is something to see, and hidden until
 * you point at the row when there is not: an empty row should not carry a badge
 * advertising its own emptiness.
 */
export function RowCardMark({
  hasNote,
  count,
  open,
  onToggle,
  note = '',
  comments = [],
}: {
  hasNote: boolean
  count: number
  open: boolean
  onToggle: () => void
  /** the row's note, for the peek. Omitted when there is none; both seats now pass
   *  whatever the row carries, because the note travels with the row. */
  note?: string
  comments?: CardComment[]
}) {
  const has = hasNote || count > 0
  const [peek, setPeek] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), [])

  const enter = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setPeek(true), PEEK_DELAY)
  }
  const leave = () => {
    if (timer.current) clearTimeout(timer.current)
    setPeek(false)
  }

  const showing = peek && !open && has

  return (
    <span className="relative inline-flex" onMouseEnter={enter} onMouseLeave={leave}>
      <button
        type="button"
        onClick={() => {
          leave()
          onToggle()
        }}
        onFocus={enter}
        onBlur={leave}
        title={
          has
            ? `${count} comment${count === 1 ? '' : 's'}${hasNote ? ' · has a note' : ''}`
            : 'Add a note or a comment'
        }
        aria-label="Note and comments"
        aria-expanded={open}
        className={`-m-1 flex items-center gap-0.5 p-1 ${has || open ? '' : 'on-hover'}`}
        style={{ color: has || open ? 'var(--marine)' : 'var(--faint)' }}
      >
        <Icon name="MessageSquare" size={14} />
        {count > 0 && (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-micro)' }}>{count}</span>
        )}
      </button>

      {showing && (
        <span className="row-peek" role="tooltip">
          {note.trim() && (
            <>
              <span className="eyebrow">Note</span>
              <Markdown body={note} className="prose-body prose-tight mt-0.5 mb-2" />
            </>
          )}
          {comments.length > 0 && (
            <>
              <span className="eyebrow">Conversation · {comments.length}</span>
              {/* The LAST two: a peek answers "what is going on here", and the end of
                  a thread answers it better than the beginning. */}
              {comments.slice(-2).map((c) => (
                <span key={c.id} className="mt-1 block">
                  <span className="flex items-baseline gap-1.5">
                    <span
                      className="text-meta font-medium"
                      style={{ color: c.mine ? 'var(--marine)' : undefined }}
                    >
                      {c.by || 'Someone'}
                    </span>
                    <span className="meta">{c.at.slice(0, 10)}</span>
                  </span>
                  <Markdown body={c.body} className="prose-body prose-tight" />
                </span>
              ))}
            </>
          )}
          <span className="note mt-2 block">Click to open it in the row</span>
        </span>
      )}
    </span>
  )
}

export function RowCardPanel({
  comments,
  noteSlot,
  composer,
  onPost,
  posting,
}: {
  comments: CardComment[]
  noteSlot: ReactNode
  /**
   * The team's composer, which is the app's one editor: mentions and all.
   *
   * A SLOT rather than a component, for the same reason `noteSlot` is one. This panel
   * is shared with the client's seat, and the mention picker reads the whole log out
   * of the store to autocomplete against — a client's seat mounts no store, and if it
   * did, the picker would offer them every org, project and person the team has.
   * So the team passes this and the client keeps the plain box below.
   */
  composer?: ReactNode
  onPost?: ((body: string) => void) | undefined
  posting?: boolean
}) {
  const [draft, setDraft] = useState('')

  const post = () => {
    const body = draft.trim()
    if (!body || !onPost) return
    onPost(body)
    setDraft('')
  }

  return (
    // `board-open` spans every column and paints the ground inside the paper; see
    // global.css. The note takes the width prose wants and the thread sits beside it,
    // stacking under its own container query when the board is narrow.
    <div className="board-open">
      <div className="board-open-cols">
        <div className="min-w-0">{noteSlot}</div>

        <div className="min-w-0">
          <p className="eyebrow mb-1">Conversation · {comments.length}</p>
          {comments.length === 0 ? (
            <p className="meta py-1">Nothing yet.</p>
          ) : (
            <div className="mb-2">
              {comments.map((c) => (
                <div
                  key={c.id}
                  className="border-b py-1.5 last:border-b-0"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <span className="flex items-baseline gap-1.5">
                    <span
                      className="text-body font-medium"
                      style={{ color: c.mine ? 'var(--marine)' : undefined }}
                    >
                      {c.by || 'Someone'}
                    </span>
                    <span className="meta">{c.at.slice(0, 10)}</span>
                  </span>
                  {/* Markdown, like the note beside it: the two halves of this panel
                      are the same kind of prose written by the same people, and a
                      list in one and literal asterisks in the other is not a
                      distinction anybody meant. */}
                  <Markdown body={c.body} className="prose-body prose-tight mt-0.5" />
                </div>
              ))}
            </div>
          )}

          {composer}
          {onPost && !composer && (
            <div className="flex items-end gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  // Enter posts; shift-Enter is a newline. A comment is usually one
                  // line, and reaching for a button to send one is the wrong default.
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    post()
                  }
                }}
                rows={2}
                placeholder="Add a comment…"
                className="prose-body rounded-control min-w-0 flex-1 resize-none border px-2 py-1.5 outline-none"
                style={{
                  borderColor: 'var(--border)',
                  background: 'var(--panel)',
                  fontSize: 'var(--text-body)',
                }}
              />
              <Button onClick={post} disabled={posting}>
                Post
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
